import { ChangeEvent, useRef, useState } from 'react'
import { ArrowLeft, Check, ChevronRight, ImagePlus, Loader, Search, Trash2, Upload } from 'lucide-react'
import { apiClient, getApiError, ImageSearchResult } from '../api'
import { ImageSelector } from './ImageSelector'
import { Toast } from './Toast'

export function ManualSearchScreen({ onBack }: { onBack: () => void }) {
  const [codigo, setCodigo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [query, setQuery] = useState('')
  const [images, setImages] = useState<ImageSearchResult[]>([])
  const [selected, setSelected] = useState<number | null>(null)
  const [offset, setOffset] = useState(0)
  const [busy, setBusy] = useState(false)
  const [localImage, setLocalImage] = useState<{ name: string; previewUrl: string; file: File } | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' | 'info' } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateCode = () => {
    if (!codigo.trim()) {
      setToast({ message: 'Informe o código do produto', type: 'error' })
      return false
    }
    return true
  }

  const search = async (nextOffset = 0) => {
    if (!validateCode()) return
    if (!descricao.trim()) {
      setToast({ message: 'Para pesquisar na internet, informe uma descrição. Para enviar imagem do computador, basta o código.', type: 'info' })
      return
    }
    try {
      setBusy(true)
      const r = await apiClient.manualSearch(codigo.trim(), descricao.trim(), nextOffset)
      setImages(r.data.images)
      setQuery(r.data.query)
      setOffset(nextOffset)
      setSelected(null)
    } catch (e) {
      setToast({ message: getApiError(e, 'Erro na pesquisa avulsa'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const finishApproval = () => {
    setToast({ message: `${codigo.trim()} aprovado como Pesquisa Avulsa`, type: 'success' })
    setImages([])
    setSelected(null)
    if (localImage?.previewUrl) URL.revokeObjectURL(localImage.previewUrl)
    setLocalImage(null)
    setQuery('')
    setOffset(0)
    setCodigo('')
    setDescricao('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const approveSearchResult = async () => {
    if (selected === null || !validateCode()) return
    try {
      setBusy(true)
      const img = images[selected]
      await apiClient.approveImage(codigo.trim(), img, query || descricao.trim() || codigo.trim(), 'MANUAL', descricao.trim() || codigo.trim())
      finishApproval()
    } catch (e) {
      setToast({ message: getApiError(e, 'Erro ao aprovar'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const handleLocalFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setToast({ message: 'Selecione um arquivo de imagem', type: 'error' })
      return
    }
    if (localImage?.previewUrl) URL.revokeObjectURL(localImage.previewUrl)
    setLocalImage({ name: file.name, previewUrl: URL.createObjectURL(file), file })
    setSelected(null)
  }

  const approveLocalImage = async () => {
    if (!localImage || !validateCode()) return
    try {
      setBusy(true)
      const label = descricao.trim() || codigo.trim()
      await apiClient.approveLocalFile(codigo.trim(), localImage.file, label, 'MANUAL', label)
      finishApproval()
    } catch (e) {
      setToast({ message: getApiError(e, 'Erro ao enviar imagem do computador'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#18181A] text-[#1F1F21] dark:text-white">
      <Toast message={toast?.message || null} type={toast?.type} />
      <header className="sticky top-0 z-40 bg-white dark:bg-[#232326] border-b border-gray-200 dark:border-[#414142]">
        <div className="max-w-6xl mx-auto p-3 sm:p-4 flex justify-between items-center gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-black truncate">Pesquisa Avulsa</h1>
            <p className="text-[11px] sm:text-xs text-gray-500">Código obrigatório. Descrição só é necessária para pesquisar na internet.</p>
          </div>
          <button onClick={onBack} className="shrink-0 px-3 sm:px-4 py-2 rounded-lg bg-[#414142] text-white font-bold flex items-center gap-2">
            <ArrowLeft size={15} /><span className="hidden sm:inline">Voltar</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4">
        <section className="bg-white dark:bg-[#232326] border border-gray-200 dark:border-[#5A5A5E] rounded-xl p-3 sm:p-4">
          <div className="grid grid-cols-1 lg:grid-cols-[160px_1fr_auto_auto] gap-2 sm:gap-3 items-stretch">
            <input value={codigo} onChange={e => setCodigo(e.target.value)} placeholder="Código *" className="px-3 py-3 rounded-lg border dark:bg-[#18181A] dark:border-[#5A5A5E]" />
            <input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descrição para pesquisa (opcional)" className="px-3 py-3 rounded-lg border dark:bg-[#18181A] dark:border-[#5A5A5E]" />
            <button onClick={() => search(0)} disabled={busy || !codigo.trim()} className="px-4 py-3 rounded-lg bg-[#FCD016] text-[#18181A] font-black flex items-center gap-2 justify-center disabled:opacity-50">
              {busy ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}Pesquisar
            </button>
            <>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLocalFile} />
              <button onClick={() => fileInputRef.current?.click()} disabled={busy || !codigo.trim()} className="px-4 py-3 rounded-lg bg-[#414142] hover:bg-[#2F2F31] text-white font-black flex items-center gap-2 justify-center disabled:opacity-50">
                <Upload size={16} />Imagem do computador
              </button>
            </>
          </div>
        </section>

        {localImage && (
          <section className="bg-white dark:bg-[#232326] border border-gray-200 dark:border-[#5A5A5E] rounded-xl p-3 sm:p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="min-w-0"><h2 className="font-black flex items-center gap-2"><ImagePlus size={18}/>Imagem selecionada</h2><p className="text-xs text-gray-500 truncate">{localImage.name}</p></div>
              <button onClick={() => { if (localImage?.previewUrl) URL.revokeObjectURL(localImage.previewUrl); setLocalImage(null); if (fileInputRef.current) fileInputRef.current.value = '' }} disabled={busy} className="shrink-0 p-2 rounded-lg border border-red-300 text-red-600"><Trash2 size={16}/></button>
            </div>
            <div className="grid sm:grid-cols-[220px_1fr] gap-4 items-center">
              <div className="bg-white rounded-xl border overflow-hidden h-48 flex items-center justify-center"><img src={localImage.previewUrl} alt={localImage.name} className="max-w-full max-h-full object-contain"/></div>
              <div><p className="text-sm text-gray-500 mb-3">Será vinculada ao código <strong>{codigo || '—'}</strong>. A descrição pode ficar vazia.</p><button onClick={approveLocalImage} disabled={busy || !codigo.trim()} className="w-full sm:w-auto px-6 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-black flex items-center gap-2 justify-center disabled:opacity-50">{busy?<Loader size={16} className="animate-spin"/>:<Check size={16}/>}Aprovar imagem do computador</button></div>
            </div>
          </section>
        )}

        {images.length > 0 && <>
          <section className="bg-white dark:bg-[#232326] rounded-xl border border-gray-200 dark:border-[#5A5A5E] p-3 sm:p-4">
            <ImageSelector images={images} selectedIndex={selected} onSelect={setSelected} isLoading={busy} />
            {selected !== null && <button onClick={approveSearchResult} disabled={busy} className="mt-4 w-full py-3 rounded-lg bg-green-600 text-white font-black flex items-center gap-2 justify-center disabled:opacity-60"><Check size={16}/>Aprovar como Pesquisa Avulsa</button>}
          </section>
          <button onClick={() => search(offset + 5)} disabled={busy} className="w-full py-3 rounded-lg bg-[#414142] text-white font-bold flex items-center gap-2 justify-center disabled:opacity-60"><ChevronRight size={16}/>Próximas 5</button>
        </>}
      </main>
    </div>
  )
}
